import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { GoTrash } from "react-icons/go";
import { IoMdAdd } from "react-icons/io";
import Pagination from "../../../components/ui/Pagination";
import ConfirmationModal from "../../../components/ui/ConfirmationModal";
import CompanyModal from "./CompanyModal";
import CompanyCard from "./CompanyCard";
import CompanyFilters from "./CompanyFilters";
import EmptyState from "./EmptyState";
import { FaRegBuilding } from "react-icons/fa";
import PageHeader from "../../../components/ui/PageHeader";
import LoadingSpinner from "../../../components/ui/LoadingSpinner";
import Button from "../../../components/ui/Button";
import { strings } from "../../../strings/strings";
import FreezeModal from "./FreezeModal";
import { DELETE_COMPANY_URL, GET_COMPANY_DETAILS_URL, GET_COMPANY_LIST_URL, SUSPEND_COMPANY_URL } from "../../../api/url";
import { apiCall } from "../../../api/fetchClient";
import { ROUTES } from "../../../routes/routes";

const CompanyPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [sortBy, setSortBy] = useState("desc");
  const [gridView, setGridView] = useState("three");
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    type: null,
    company: null,
  });
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    totalPages: 1,
    totalCompanies: 0,
    hasNextPage: false,
    hasPrevPage: false,
  });
  const [freezeModal, setFreezeModal] = useState({
    isOpen: false,
    company: null,
  });

  const searchDebounceRef = useRef(null);
  const fetchCompanies = async () => {
    try {
      setLoading(true);

      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: itemsPerPage.toString(),
        sortOrder: sortBy,
      });

      if (searchQuery) {
        params.append("search", searchQuery);
      }

      if (selectedStatus !== "all") {
        params.append("status", selectedStatus);
      }

      const response = await apiCall(
        `${GET_COMPANY_LIST_URL}?${params.toString()}`,
        "GET",
      );
      if (!!response.success) {
        setCompanies(response?.results?.companies || []);
        setPagination({
          totalPages: response?.results?.pagination?.totalPages || 1,
          totalCompanies: response?.results?.pagination?.totalCompanies || 0,
          hasNextPage: response?.results?.pagination?.hasNextPage || false,
          hasPrevPage: response?.results?.pagination?.hasPrevPage || false,
        });

        setLoading(false);
      }
    } catch (error) {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }

    const delay = searchQuery ? 500 : 0;

    searchDebounceRef.current = setTimeout(() => {
      fetchCompanies();
    }, delay);

    // Cleanup
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, [currentPage, selectedStatus, sortBy, searchQuery, itemsPerPage]);
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, selectedStatus, sortBy, itemsPerPage]);

  useEffect(() => {
    if (location.state?.openAddModal) {
      setIsModalOpen(true);
      window.history.replaceState({}, document.title);
    }

    if (location.state?.status) {
      setSelectedStatus(location.state.status);
      window.history.replaceState({}, document.title);
    }
    
    if (location.state?.focusSearch) {
      setTimeout(() => {
        const searchInput = document.getElementById('company-search-input-desktop') || document.getElementById('company-search-input');
        if (searchInput) {
          searchInput.focus();
        }
      }, 300);
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  const handleSaveCompany = (companyData) => {
    fetchCompanies();
    closeModal();
  };

  const handleDelete = async () => {
    try {
      const response = await apiCall(
        `${DELETE_COMPANY_URL}${confirmModal.company._id}`,
        "DELETE",
      );

      if (response.success) {
        fetchCompanies();
        closeConfirmModal();
      }
    } catch (error) {

      closeConfirmModal();
    }
  };

  const handleFreeze = async (reason) => {
    try {
      const isSuspending = freezeModal.company?.isActive;
      let requestBody;

      if (isSuspending) {
        requestBody = {
          isSuspended: true,
          reason: reason
        };
      } else {
        requestBody = {
          isSuspended: false
        };
      }

      const response = await apiCall(
        `${SUSPEND_COMPANY_URL}${freezeModal.company._id}`,
        'PUT',
        requestBody
      );

      if (response.success) {
        fetchCompanies();
        closeFreezeModal();
      }
    } catch (error) {
      closeFreezeModal();
    }
  };

  const closeFreezeModal = () => {
    setFreezeModal({ isOpen: false, company: null });
  };

  const openModal = async (company = null) => {
    if (company) {
      try {
        const response = await apiCall(
          `${GET_COMPANY_DETAILS_URL}${company._id}`,
          "GET",
        );
        if (response.success) {
          setSelectedCompany(response.results.company);
          setIsModalOpen(true);
        }
      } catch (error) {
      }
    } else {
      setSelectedCompany(null);
      setIsModalOpen(true);
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedCompany(null);
  };

  const openConfirmModal = (type, company) => {
    if (type === "freeze") {
      setFreezeModal({ isOpen: true, company });
    } else {
      setConfirmModal({ isOpen: true, type, company });
    }
  };

  const closeConfirmModal = () => {
    setConfirmModal({ isOpen: false, type: null, company: null });
  };

  const handleViewCompany = (company) => {
    const companyId = company?._id || company?.id;
    if (companyId) {
      navigate(ROUTES.ADMIN_COMPANY_DETAILS.replace(":companyId", companyId));
    }
  };

  return (
    <div className="min-h-screen">
      <PageHeader
        title={strings.COMPANY_MANAGEMENT || strings.COMPANEE_MANAGEMENT}
        description={strings.COMPANY_DESCRIPTION}
        icon={FaRegBuilding}
      >
        <Button onClick={() => openModal()}>
          <IoMdAdd className="w-4 h-4 mr-2" />
          {strings.ADD_COMPANY}
        </Button>
      </PageHeader>
      <CompanyFilters
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        selectedStatus={selectedStatus}
        setSelectedStatus={setSelectedStatus}
        sortBy={sortBy}
        setSortBy={setSortBy}
        gridView={gridView}
        setGridView={setGridView}
        totalCompanies={pagination.totalCompanies}
      />

      {loading ? (
        <LoadingSpinner message="Loading companies..." minHeight="h-64" />
      ) : companies.length > 0 ? (
        <div
          className={`grid gap-6 mb-6 ${gridView === "one"
            ? "grid-cols-1"
            : gridView === "two"
              ? "grid-cols-1 lg:grid-cols-2"
              : gridView === "three"
                ? "grid-cols-1 md:grid-cols-2 xl:grid-cols-3"
                : "grid-cols-1"
            }`}
        >
          {companies.map((company) => (
            <CompanyCard
              key={company._id}
              company={company}
              onView={handleViewCompany}
              onEdit={openModal}
              onFreeze={(c) => openConfirmModal("freeze", c)}
              onDelete={(c) => openConfirmModal("delete", c)}
              isOneColumn={gridView === "one"}
            />
          ))}
        </div>
      ) : (
        <EmptyState
          searchQuery={searchQuery}
          selectedStatus={selectedStatus}
          onAddCompany={() => openModal()}
        />
      )}

      {pagination.totalCompanies > 0 && (
        <Pagination
          currentPage={currentPage}
          totalPages={pagination.totalPages}
          onPageChange={setCurrentPage}
          totalItems={pagination.totalCompanies}
          itemsPerPage={itemsPerPage}
          onItemsPerPageChange={(newLimit) => {
            setItemsPerPage(newLimit);
            setCurrentPage(1);
          }}
          itemsPerPageOptions={[10, 20, 50, 100]}
        />
      )}
      <CompanyModal
        isOpen={isModalOpen}
        onClose={closeModal}
        onSave={handleSaveCompany}
        company={selectedCompany}
      />

      <FreezeModal
        isOpen={freezeModal.isOpen}
        onClose={closeFreezeModal}
        onConfirm={handleFreeze}
        company={freezeModal.company}
      />
      <ConfirmationModal
        isOpen={confirmModal.isOpen}
        onClose={closeConfirmModal}
        onConfirm={handleDelete}
        title={strings.DELETE_TITLE}
        message={strings.DELETE_MESSAGE(confirmModal.company?.companyName)}
        confirmText={strings.DELETE}
        variant="danger"
        icon={GoTrash}
      />
    </div>
  );
};

export default CompanyPage;
